import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  KeyboardEvent,
  ReactNode,
  SelectHTMLAttributes,
} from 'react'
import { createContext, forwardRef, useContext, useEffect, useState } from 'react'
import { cn } from '../lib/cn.js'
import { useIsField } from '../lib/media.js'
import { Icon, type IconName } from './Icon.js'

/**
 * The interface kit.
 *
 * Density is not decided here. Every control sizes itself from the
 * `--control-*` and `--row-pad-*` tokens declared in `styles.css`, which are
 * condensed on a pointer and back at the 44px touch floor below 1024px. That
 * means this file contains no breakpoint: the office screen is dense and the
 * phone in the van is tappable, from one set of class names.
 *
 * The look is the job card's (ADR 025), carried through the app (ADR 030):
 * a Buster Navy command bar on top of a screen, Archivo navy headings, and
 * Buster Red only for the bar's lead action, the current tab or section, and
 * today.
 *
 * Two colour rules, both easy to break by accident:
 *
 * - Green means "do this". Buster Navy (`brand-800`) means "this is on". A
 *   selected filter chip or a current nav item is navy, never green.
 * - Status tones come from the lookup maps at the bottom. Never pick one by
 *   eye, and never invent a seventh.
 *
 * Composition goes through `cn`, never bare `clsx`: a caller's `className` has
 * to be able to override the base one, and concatenation leaves that to CSS
 * source order.
 */

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
type Size = 'sm' | 'md' | 'lg'

const buttonVariants: Record<ButtonVariant, string> = {
  primary: 'bg-brand-600 text-white hover:bg-brand-700 active:bg-brand-700',
  secondary: 'bg-white text-ink-800 ring-1 ring-inset ring-ink-300 hover:bg-ink-50',
  ghost: 'text-ink-600 hover:bg-ink-100 hover:text-ink-900',
  danger: 'bg-red-600 text-white hover:bg-red-700',
}

/** The job card header's chips: the lead action is Book's red. */
const navyButtonVariants: Record<ButtonVariant, string> = {
  primary: 'bg-accent-500 font-bold text-white hover:bg-accent-700 active:bg-accent-700',
  secondary: 'bg-white/[0.08] font-semibold text-white hover:bg-white/15',
  ghost: 'text-white/80 hover:bg-white/10 hover:text-white',
  danger: 'bg-red-600 text-white hover:bg-red-700',
}

/**
 * Whether a control is drawn on a `CommandBar`'s navy. The kit's own controls
 * read it; anything hand-drawn inside a bar asks `useOnNavy`.
 */
const NavySurface = createContext(false)

export function useOnNavy(): boolean {
  return useContext(NavySurface)
}

const buttonSizes: Record<Size, string> = {
  sm: 'min-h-[var(--control-sm)] px-[var(--control-pad-sm)] text-xs gap-1.5',
  md: 'min-h-[var(--control-md)] px-[var(--control-pad-md)] text-sm gap-1.5',
  lg: 'min-h-[var(--control-lg)] px-[var(--control-pad-lg)] rounded-lg text-sm gap-2',
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: Size
  loading?: boolean
  icon?: ReactNode
  block?: boolean
}

/**
 * The button skin on its own, for the handful of places where the control has
 * to be an anchor: `tel:` on the field screens, and router links that must
 * still middle-click to a new tab. A `<button>` with an onClick navigate is
 * not a link and behaves wrong under every modifier key.
 */
export function buttonClass({
  variant = 'secondary',
  size = 'md',
  block,
  onNavy = false,
  className,
}: {
  variant?: ButtonVariant
  size?: Size
  block?: boolean
  onNavy?: boolean
  className?: string
} = {}): string {
  return cn(
    'inline-flex items-center justify-center rounded-md font-medium whitespace-nowrap transition-colors',
    'disabled:cursor-not-allowed disabled:opacity-50',
    (onNavy ? navyButtonVariants : buttonVariants)[variant],
    buttonSizes[size],
    block && 'w-full',
    className,
  )
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', loading, icon, block, className, children, disabled, ...props },
  ref,
) {
  const onNavy = useOnNavy()
  return (
    <button
      ref={ref}
      // A button mid-submit stays disabled, so a double tap on a slow
      // connection cannot raise two invoices.
      disabled={disabled || loading}
      className={buttonClass({ variant, size, block, onNavy, className })}
      {...props}
    >
      {loading ? <Spinner className="size-4" /> : icon}
      {children}
    </button>
  )
})

export function Spinner({ className }: { className?: string }) {
  return (
    <svg
      className={cn('animate-spin', className)}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
      />
    </svg>
  )
}

export interface FieldProps {
  label: string
  hint?: string
  error?: string | string[]
  required?: boolean
  children: ReactNode
  htmlFor?: string
  /**
   * Sits opposite the label — "Forgotten it?" beside Password. It belongs on
   * the label row rather than under the button, because it answers a question
   * you have while looking at the field, not after giving up on it.
   */
  action?: ReactNode
}

export function Field({ label, hint, error, required, children, htmlFor, action }: FieldProps) {
  const message = Array.isArray(error) ? error[0] : error

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-baseline justify-between gap-2">
        <label htmlFor={htmlFor} className="block text-xs font-medium text-ink-700">
          {label}
          {required ? <span className="ml-0.5 text-red-600">*</span> : null}
        </label>
        {action}
      </div>
      {children}
      {/* The error replaces the hint rather than stacking, so the field does
          not reflow and push the submit button under the thumb. */}
      {message ? (
        <p className="text-xs text-red-600" role="alert">
          {message}
        </p>
      ) : hint ? (
        <p className="text-xs text-ink-500">{hint}</p>
      ) : null}
    </div>
  )
}

const controlStyles =
  'block w-full rounded-md border-0 bg-white px-2.5 text-sm text-ink-900 ring-1 ring-inset ' +
  'ring-ink-300 placeholder:text-ink-400 focus:outline-none focus:ring-2 focus:ring-inset ' +
  'focus:ring-brand-600 disabled:bg-ink-100 disabled:text-ink-500 min-h-[var(--control-md)]'

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...props }, ref) {
    return <input ref={ref} className={cn(controlStyles, className)} {...props} />
  },
)

/**
 * A password box with a reveal and a Caps Lock warning.
 *
 * Both exist for the same reason: nobody can see what they typed. A technician
 * entering a twelve-character password on a phone keyboard, in the sun, with
 * one hand, gets it wrong often enough that "wrong password" is a worse answer
 * than letting them look. The reveal keeps the caret where it was — a button
 * that steals focus means clicking back into the field to finish the word.
 *
 * Caps Lock shows as a chip inside the box rather than a line under it, so a
 * warning appearing does not reflow the form and move the submit button out
 * from under a thumb — the same reason `Field` swaps the hint for the error
 * instead of stacking them.
 */
export const PasswordInput = forwardRef<
  HTMLInputElement,
  Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>
>(function PasswordInput({ className, onKeyDown, onKeyUp, onBlur, ...props }, ref) {
  const [visible, setVisible] = useState(false)
  const [capsLock, setCapsLock] = useState(false)

  const readCapsLock = (event: KeyboardEvent<HTMLInputElement>) => {
    // A soft keyboard reports no modifier state at all, and neither does
    // jsdom. No news is "off" rather than a crash.
    if (typeof event.getModifierState !== 'function') return
    setCapsLock(event.getModifierState('CapsLock'))
  }

  return (
    <div className="relative">
      <input
        ref={ref}
        type={visible ? 'text' : 'password'}
        className={cn(controlStyles, capsLock ? 'pr-[5.25rem]' : 'pr-10', className)}
        onKeyDown={(event) => {
          readCapsLock(event)
          onKeyDown?.(event)
        }}
        onKeyUp={(event) => {
          readCapsLock(event)
          onKeyUp?.(event)
        }}
        onBlur={(event) => {
          setCapsLock(false)
          onBlur?.(event)
        }}
        {...props}
      />

      <div className="absolute inset-y-0 right-0 flex items-center">
        <span aria-live="polite">
          {capsLock ? (
            <span className="rounded-sm bg-amber-50 px-1 py-px text-[10px] font-semibold text-amber-800 ring-1 ring-inset ring-amber-200">
              Caps Lock
            </span>
          ) : null}
        </span>

        <button
          type="button"
          aria-pressed={visible}
          aria-label={visible ? 'Hide password' : 'Show password'}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => setVisible((current) => !current)}
          className="grid h-full w-[var(--control-md)] place-items-center rounded-md text-ink-400 transition-colors hover:text-ink-800"
        >
          <Icon name={visible ? 'eyeOff' : 'eye'} className="size-4" />
        </button>
      </div>
    </div>
  )
})

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, children, ...props }, ref) {
    return (
      <select ref={ref} className={cn(controlStyles, 'pr-8', className)} {...props}>
        {children}
      </select>
    )
  },
)

export function Textarea({
  className,
  ...props
}: React.ComponentProps<'textarea'>) {
  return <textarea className={cn(controlStyles, 'min-h-18 py-2', className)} {...props} />
}

/**
 * A card is white, a hairline ring and no shadow. A dense screen of shadowed
 * cards reads as noise.
 *
 * Every card is the dispatch board's card — 14px radius and an inset ring,
 * from the `.board-card` rule in `styles.css` — because every screen now sits
 * on the same ink-100 field. `variant` survives for callers that still say
 * `board`; the two used to differ and no longer do.
 */
export function Card({
  variant = 'default',
  className,
  children,
}: {
  variant?: 'default' | 'board'
  className?: string
  children: ReactNode
}) {
  return (
    <div
      data-variant={variant}
      className={cn('board-card overflow-hidden bg-white', className)}
    >
      {children}
    </div>
  )
}

export function CardHeader({
  title,
  action,
  description,
}: {
  title: ReactNode
  action?: ReactNode
  description?: ReactNode
}) {
  // The job card's section heading (`Label` in `job-card/panes.tsx`).
  const heading = (
    <h2 className="font-display text-[15px] leading-snug font-bold text-brand-800">{title}</h2>
  )

  return (
    <div className="flex min-h-[var(--card-head)] items-center justify-between gap-2 border-b border-ink-100 px-[var(--card-pad)] py-1">
      {description ? (
        <div className="min-w-0">
          {heading}
          <p className="mt-0.5 text-xs text-ink-500">{description}</p>
        </div>
      ) : (
        heading
      )}
      {action}
    </div>
  )
}

export type Tone = 'neutral' | 'blue' | 'green' | 'amber' | 'red' | 'purple'

/** Exported so anything drawing its own tinted chip agrees with the badges. */
export const toneStyles: Record<Tone, string> = {
  neutral: 'bg-ink-100 text-ink-700 ring-ink-200',
  blue: 'bg-blue-50 text-blue-700 ring-blue-200',
  green: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  amber: 'bg-amber-50 text-amber-800 ring-amber-200',
  red: 'bg-red-50 text-red-700 ring-red-200',
  purple: 'bg-purple-50 text-purple-700 ring-purple-200',
}

export function Badge({
  tone = 'neutral',
  children,
  className,
}: {
  tone?: Tone
  children: ReactNode
  className?: string
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-sm px-1.5 py-px text-[11px] font-medium ring-1 ring-inset whitespace-nowrap',
        toneStyles[tone],
        className,
      )}
    >
      {children}
    </span>
  )
}

/**
 * The pill toggle. Written inline and identically in the jobs and inbox
 * filters before this existed, so the two could drift.
 *
 * Selected is navy on white, not green: green means "do this". On a
 * command bar it turns over, white on navy.
 */
export function FilterChip({
  active,
  onClick,
  children,
  className,
}: {
  active: boolean
  onClick: () => void
  children: ReactNode
  className?: string
}) {
  const onNavy = useOnNavy()
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'inline-flex min-h-[var(--control-sm)] items-center rounded-full px-2 text-[11px] font-medium transition-colors',
        onNavy
          ? active
            ? 'bg-white font-semibold text-brand-800'
            : 'bg-white/[0.08] text-white hover:bg-white/15'
          : active
            ? 'bg-brand-800 font-semibold text-white ring-1 ring-inset ring-brand-800'
            : 'bg-white text-ink-600 ring-1 ring-inset ring-ink-300 hover:bg-ink-50',
        className,
      )}
    >
      {children}
    </button>
  )
}

/**
 * An on/off switch, for a setting that takes effect the moment it moves.
 *
 * `role="switch"` and not a checkbox: there is no form under it and nothing to
 * submit, so "on / off" is the honest reading rather than "ticked". Use it when
 * a thing is either happening or not. Where the choice is between two named
 * alternatives that are both something — status or category, day or timeline —
 * a segmented control still says more than a switch called "Category".
 *
 * On is **Buster Navy**, per the colour rule at the top of this file. A switch
 * is reporting a state, not offering an action, so it never goes green.
 *
 * The track is 24px, which is under the touch floor, so the button around it
 * takes `--control-sm` and the extra height is hit area. `label` is the
 * accessible name — draw the visible text beside it, and the two agreeing is
 * the point.
 */
export function Toggle({
  checked,
  onChange,
  label,
  disabled = false,
  size = 'md',
  className,
}: {
  checked: boolean
  onChange: (next: boolean) => void
  label: string
  disabled?: boolean
  /** `sm` is a 16px track for a toolbar, beside 12px text. */
  size?: 'md' | 'sm'
  className?: string
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'inline-flex min-h-[var(--control-sm)] shrink-0 items-center',
        disabled ? 'cursor-default' : 'cursor-pointer',
        className,
      )}
    >
      <SwitchTrack checked={checked} disabled={disabled} size={size} />
    </button>
  )
}

/** The drawn half of `Toggle`, for a row that is itself the switch. */
export function SwitchTrack({
  checked,
  disabled = false,
  size = 'md',
}: {
  checked: boolean
  disabled?: boolean
  size?: 'md' | 'sm'
}) {
  const small = size === 'sm'
  return (
    <span
      aria-hidden="true"
      className={cn(
        'relative shrink-0 rounded-full transition-colors',
        small ? 'h-4 w-7' : 'h-6 w-10',
        checked ? 'bg-brand-800' : 'bg-ink-300',
        disabled ? 'opacity-50' : checked ? 'hover:bg-brand-700' : 'hover:bg-ink-400',
      )}
    >
      <span
        className={cn(
          'absolute rounded-full bg-white shadow-sm transition-transform',
          small ? 'top-[2px] left-[2px] size-3' : 'top-[3px] left-[3px] size-[18px]',
          checked && (small ? 'translate-x-3' : 'translate-x-4'),
        )}
      />
    </span>
  )
}


/**
 * Status colours are defined once, so a status never means two things.
 *
 * Mapped to the ServiceM8 colours people already know: Quote orange, work
 * (formerly Work Order) blue, Complete green, Unsuccessful red.
 */
export const JOB_STATUS_TONE: Record<string, Tone> = {
  LEAD: 'neutral',
  QUOTING: 'amber',
  QUOTED: 'amber',
  APPROVED: 'blue',
  SCHEDULED: 'blue',
  IN_PROGRESS: 'blue',
  ON_HOLD: 'neutral',
  AWAITING_PARTS: 'amber',
  COMPLETED: 'green',
  CANCELLED: 'red',
}

/** Hex for board borders and anything else that cannot use a tone class. */
export const JOB_STATUS_COLOUR: Record<string, string> = {
  LEAD: '#64748b',
  QUOTING: '#f59e0b',
  QUOTED: '#f59e0b',
  APPROVED: '#2563eb',
  SCHEDULED: '#2563eb',
  IN_PROGRESS: '#2563eb',
  ON_HOLD: '#64748b',
  AWAITING_PARTS: '#d97706',
  COMPLETED: '#16a34a',
  CANCELLED: '#dc2626',
}

/**
 * How a job status reads to a person.
 *
 * ServiceM8's Work Order is "In Progress" here; Cancelled is "Unsuccessful".
 * Quote / Complete keep the words the board already trained people on.
 * Approved, Scheduled and In Progress all surface as "In Progress" on the
 * board — they are the same ServiceM8 Work Order colour.
 */
export function jobStatusLabel(status: string): string {
  switch (status) {
    case 'QUOTING':
    case 'QUOTED':
      return 'Quote'
    case 'APPROVED':
    case 'SCHEDULED':
    case 'IN_PROGRESS':
      return 'In Progress'
    case 'COMPLETED':
      return 'Complete'
    case 'CANCELLED':
      return 'Unsuccessful'
    default:
      return humanise(status)
  }
}

/**
 * Labels for filters and status pickers, where Approved / Scheduled /
 * In Progress must stay distinguishable even though the board collapses them.
 */
export function jobStatusOptionLabel(status: string): string {
  switch (status) {
    case 'QUOTING':
      return 'Quote'
    case 'APPROVED':
      return 'In Progress'
    case 'COMPLETED':
      return 'Complete'
    case 'CANCELLED':
      return 'Unsuccessful'
    default:
      return humanise(status)
  }
}

export const INVOICE_STATUS_TONE: Record<string, Tone> = {
  DRAFT: 'neutral',
  ISSUED: 'blue',
  PARTIALLY_PAID: 'amber',
  PAID: 'green',
  OVERDUE: 'red',
  VOID: 'neutral',
  WRITTEN_OFF: 'neutral',
}

export const QUOTE_STATUS_TONE: Record<string, Tone> = {
  DRAFT: 'neutral',
  SENT: 'blue',
  VIEWED: 'purple',
  ACCEPTED: 'green',
  DECLINED: 'red',
  EXPIRED: 'amber',
  SUPERSEDED: 'neutral',
}

export const VISIT_STATUS_TONE: Record<string, Tone> = {
  PROPOSED: 'neutral',
  CONFIRMED: 'blue',
  EN_ROUTE: 'amber',
  ON_SITE: 'purple',
  COMPLETED: 'green',
  CANCELLED: 'red',
  NO_SHOW: 'red',
}

export const PRIORITY_TONE: Record<string, Tone> = {
  LOW: 'neutral',
  NORMAL: 'neutral',
  HIGH: 'amber',
  EMERGENCY: 'red',
}

/** Turns SCREAMING_SNAKE into something a person would say. */
export function humanise(value: string): string {
  return value.charAt(0) + value.slice(1).toLowerCase().replace(/_/g, ' ')
}

export function EmptyState({
  title,
  description,
  action,
  icon,
}: {
  title: string
  description?: string
  action?: ReactNode
  /** A glyph from the icon set, drawn in a soft tile above the title. */
  icon?: IconName
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-1 px-6 py-6 text-center">
      {icon ? (
        <span className="mb-2 grid size-10 place-items-center rounded-xl bg-brand-50 text-brand-800">
          <Icon name={icon} />
        </span>
      ) : null}
      <p className="font-display text-[15px] font-bold text-brand-800">{title}</p>
      {description ? <p className="max-w-[340px] text-xs text-ink-500">{description}</p> : null}
      {action ? <div className="mt-3">{action}</div> : null}
    </div>
  )
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-6 text-center">
      <p className="text-sm font-medium text-red-700">{message}</p>
      {onRetry ? (
        <Button size="sm" onClick={onRetry}>
          Try again
        </Button>
      ) : null}
    </div>
  )
}

/**
 * A skeleton rather than a spinner for lists.
 *
 * A spinner tells you to wait; a skeleton tells you what is coming and stops
 * the page jumping when it arrives.
 */
export function SkeletonRows({ rows = 6 }: { rows?: number }) {
  return (
    <div className="divide-y divide-ink-100" aria-hidden="true">
      {Array.from({ length: rows }, (_, index) => (
        <div
          key={index}
          className="flex items-center gap-3 px-[var(--row-pad-x)] py-[var(--row-pad-y)]"
        >
          <div className="h-3 w-16 animate-pulse rounded-sm bg-ink-200" />
          <div className="h-3 flex-1 animate-pulse rounded-sm bg-ink-100" />
          <div className="h-3 w-24 animate-pulse rounded-sm bg-ink-100" />
        </div>
      ))}
    </div>
  )
}

export function Avatar({
  name,
  colour,
  size = 'md',
  className,
}: {
  name: string
  colour?: string
  size?: Size
  className?: string
}) {
  const dimensions = {
    sm: 'size-5 text-[9px]',
    md: 'size-7 text-[10px]',
    lg: 'size-9 text-xs',
  }[size]

  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full font-semibold text-white',
        dimensions,
        className,
      )}
      style={{ backgroundColor: colour ?? '#64748b' }}
      title={name}
    >
      {initialsOf(name)}
    </span>
  )
}

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('')
}

/**
 * Adjacent controls sharing one outline.
 *
 * Filter, sort and group are one decision — how this list reads — so they are
 * one object on the toolbar rather than three loose buttons competing with the
 * search box and the primary action for attention.
 */
export function ButtonGroup({
  children,
  className,
  label,
}: {
  children: ReactNode
  className?: string
  label?: string
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className={cn(
        'inline-flex divide-x divide-ink-300 overflow-hidden rounded-md ring-1 ring-inset ring-ink-300',
        className,
      )}
    >
      {children}
    </div>
  )
}

/**
 * The toolbar's search box.
 *
 * Debounced by 250ms and echoing whatever it is given, so the URL can own the
 * term without a keystroke costing a request. Lives here rather than beside the
 * first list that needed one, because three toolbars now use it.
 */
export function SearchBox({
  value,
  onChange,
  placeholder,
  className,
}: {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
}) {
  const [draft, setDraft] = useState(value)

  // Keeps the box in step when the term changes from elsewhere — the back
  // button, or a shared link opened in place.
  useEffect(() => setDraft(value), [value])

  useEffect(() => {
    if (draft === value) return
    const timer = setTimeout(() => onChange(draft), 250)
    return () => clearTimeout(timer)
  }, [draft, value, onChange])

  return (
    <div className={cn('relative w-full sm:w-64', className)}>
      <Icon
        name="search"
        className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-ink-400"
      />
      <Input
        type="search"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        placeholder={placeholder}
        className="w-full rounded-lg pl-8"
        aria-label={placeholder ?? 'Search'}
      />
    </div>
  )
}

/**
 * The filter strip above a list. It carries a bottom hairline because it
 * normally sits at the top of a card with rows under it; a toolbar that is a
 * card of its own passes `border-b-0`.
 */
export function Toolbar({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-1.5 border-b border-ink-200 bg-white px-[var(--row-pad-x)] py-2',
        className,
      )}
    >
      {children}
    </div>
  )
}

/**
 * The job card's header, for a screen: Buster Navy at a desk, with the
 * screen's name and the actions that belong to all of it. The kit's controls
 * inside turn over to the card header's chips: the lead action is Book's red
 * and the rest are white on navy. A hand-drawn control asks `useOnNavy`.
 *
 * The field app draws no bar. Its shell already says where you are, and what
 * is left sits on the page as it did. Decision 93, ADR 030.
 */
export function CommandBar({ className, children }: { className?: string; children: ReactNode }) {
  const field = useIsField()
  return (
    <NavySurface.Provider value={!field}>
      <div
        className={cn(
          'flex flex-wrap items-center justify-between gap-x-4 gap-y-2',
          !field && 'min-h-14 rounded-[14px] bg-brand-800 px-4 py-2 text-white',
          className,
        )}
      >
        {children}
      </div>
    </NavySurface.Provider>
  )
}

/** A command bar's title, the job number's Archivo ExtraBold. */
export function CommandTitle({ className, children }: { className?: string; children: ReactNode }) {
  const onNavy = useOnNavy()
  return (
    <h1
      className={cn(
        onNavy
          ? 'font-display text-[20px] leading-tight font-extrabold tracking-[0.01em] text-white'
          : 'text-lg font-semibold tracking-tight text-ink-900',
        className,
      )}
    >
      {children}
    </h1>
  )
}

/** The line under a command bar's title, in Sky like the job's client · suburb. */
export function CommandMeta({ className, children }: { className?: string; children: ReactNode }) {
  const onNavy = useOnNavy()
  return (
    <div
      className={cn(
        onNavy ? 'text-[13px] font-medium text-sky' : 'text-xs text-ink-500',
        className,
      )}
    >
      {children}
    </div>
  )
}

/** A screen's title and its one action, for a screen with nothing else to say. */
export function PageTitle({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <CommandBar>
      <CommandTitle>{children}</CommandTitle>
      {action}
    </CommandBar>
  )
}

/**
 * The page column. Letterbox screens share one width: 1440px, the same cap
 * the Jobs list and Settings already used. Narrower sizes stay on the type
 * for a screen that truly cannot use the room (none do today).
 *
 * `field` puts the column on the board's `ink-100` page instead of the shell's
 * `ink-50`. That is the whole difference between a screen that reads as part of
 * the dispatch board and one that reads as a different product: the icon rail
 * is `ink-100`, so a white card wants the same field beside it.
 */
export function Page({
  width = 'xwide',
  field = false,
  className,
  children,
}: {
  width?: 'narrow' | 'medium' | 'wide' | 'xwide'
  field?: boolean
  className?: string
  children: ReactNode
}) {
  const max = {
    narrow: 'max-w-[720px]',
    medium: 'max-w-[1024px]',
    wide: 'max-w-[1152px]',
    xwide: 'max-w-[1440px]',
  }[width]

  const column = (
    <div
      className={cn(
        'mx-auto flex w-full flex-col gap-[var(--stack-gap)] p-[var(--page-pad)]',
        max,
        className,
      )}
    >
      {children}
    </div>
  )

  return field ? <div className="min-h-full bg-ink-100">{column}</div> : column
}

export function Pagination({
  page,
  pageCount,
  total,
  onChange,
}: {
  page: number
  pageCount: number
  total: number
  onChange: (page: number) => void
}) {
  if (pageCount <= 1) {
    return (
      <div className="px-[var(--row-pad-x)] py-2 text-xs text-ink-500">
        {total} {total === 1 ? 'result' : 'results'}
      </div>
    )
  }

  return (
    <div className="flex items-center justify-between gap-2 border-t border-ink-200 px-[var(--row-pad-x)] py-2">
      <p className="text-xs text-ink-500">
        Page <span className="tabular-nums">{page}</span> of{' '}
        <span className="tabular-nums">{pageCount}</span> ·{' '}
        <span className="tabular-nums">{total}</span> results
      </p>
      <div className="flex gap-1.5">
        <Button
          size="sm"
          disabled={page <= 1}
          onClick={() => onChange(page - 1)}
          icon={<Icon name="back" className="size-4" />}
        >
          Previous
        </Button>
        <Button size="sm" disabled={page >= pageCount} onClick={() => onChange(page + 1)}>
          Next
          <Icon name="forward" className="size-4" />
        </Button>
      </div>
    </div>
  )
}

/**
 * The top of a screen: what it is, one line about what is in it, and the
 * actions that belong to the whole page.
 *
 * On a phone the shell's own header already carries the title, so only the
 * description and the actions are drawn — they used to vanish with the title,
 * which is how "New client" and "New job" went missing on a handset.
 *
 * At a desk it is a `CommandBar`, title over description the way the job
 * card has the number over the client.
 */
export function PageHeader({
  title,
  description,
  actions,
  eyebrow,
}: {
  title: ReactNode
  description?: ReactNode
  actions?: ReactNode
  /** A small line above the title, for a record's number or its parent. */
  eyebrow?: ReactNode
}) {
  return (
    <CommandBar>
      <div className="min-w-0">
        {eyebrow ? (
          <div className="hidden text-xs font-medium text-sky/80 desk:block">{eyebrow}</div>
        ) : null}
        <CommandTitle className="hidden desk:block">{title}</CommandTitle>
        {description ? <CommandMeta>{description}</CommandMeta> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </CommandBar>
  )
}

/**
 * A row of mutually exclusive choices, drawn as the board's pill: Jobs' stage
 * tabs, the board's view switcher, a money screen's status.
 *
 * Selected is navy, never green — this is "which one is on", not "do this".
 * On a command bar the pill turns over, white on navy.
 */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
  className,
}: {
  value: T
  options: ReadonlyArray<{ value: T; label: ReactNode; count?: number; title?: string }>
  onChange: (value: T) => void
  label: string
  className?: string
}) {
  const onNavy = useOnNavy()
  return (
    <div
      role="tablist"
      aria-label={label}
      className={cn(
        'flex w-full items-center gap-0.5 overflow-x-auto rounded-full p-0.5 sm:w-auto sm:shrink-0',
        onNavy ? 'bg-white/[0.08]' : 'bg-ink-100',
        className,
      )}
    >
      {options.map((option) => {
        const selected = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={selected}
            title={option.title}
            onClick={() => onChange(option.value)}
            className={cn(
              'flex min-h-[var(--control-sm)] flex-1 items-center justify-center gap-1.5 rounded-full px-3 text-xs whitespace-nowrap transition-colors sm:flex-none',
              onNavy
                ? selected
                  ? 'bg-white font-semibold text-brand-800 shadow-sm'
                  : 'font-medium text-white/80 hover:bg-white/10 hover:text-white'
                : selected
                  ? 'bg-brand-800 font-semibold text-white shadow-sm'
                  : 'font-medium text-ink-600 hover:bg-white hover:text-brand-800',
            )}
          >
            {option.label}
            {option.count !== undefined ? (
              <span
                className={cn(
                  'text-[11px] tabular-nums',
                  selected ? (onNavy ? 'text-brand-800/60' : 'text-white/70') : onNavy ? 'text-white/50' : 'text-ink-400',
                )}
              >
                {option.count.toLocaleString('en-AU')}
              </span>
            ) : null}
          </button>
        )
      })}
    </div>
  )
}

/**
 * A record's tabs, drawn as the job card's: bold navy with a Buster Red
 * underline for the open one, and navy counts. For the parts of one record —
 * a person's Details and Schedule. A choice of how a list reads is
 * `Segmented`.
 */
export function Tabs<T extends string>({
  value,
  options,
  onChange,
  label,
  className,
}: {
  value: T
  options: ReadonlyArray<{ value: T; label: ReactNode; count?: number; title?: string }>
  onChange: (value: T) => void
  label: string
  className?: string
}) {
  return (
    <div
      role="tablist"
      aria-label={label}
      className={cn(
        'flex items-center gap-1 overflow-x-auto border-b border-ink-200 px-[var(--row-pad-x)]',
        className,
      )}
    >
      {options.map((option) => {
        const selected = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={selected}
            title={option.title}
            onClick={() => onChange(option.value)}
            className={cn(
              'flex min-h-10 items-center gap-1.5 px-3 text-[13.5px] whitespace-nowrap transition-colors',
              selected
                ? 'font-bold text-brand-800 shadow-[inset_0_-3px_0_var(--color-accent-500)]'
                : 'font-semibold text-ink-600 hover:text-brand-800',
            )}
          >
            {option.label}
            {option.count !== undefined ? (
              <span
                className={cn(
                  'rounded-full px-1.5 py-px text-[11px] font-bold tabular-nums',
                  selected ? 'bg-brand-800 text-white' : 'bg-brand-50 text-brand-800',
                )}
              >
                {option.count.toLocaleString('en-AU')}
              </span>
            ) : null}
          </button>
        )
      })}
    </div>
  )
}

/**
 * A headline figure. The board's pulse tiles in miniature: an 11px label over
 * an 18px number with its caption beside it, so a strip of them is one short
 * band rather than a block the list starts under.
 *
 * Clickable when it filters the list below it, because a figure that says
 * "3 overdue" and does not take you to them is a dead end.
 */
export function StatTile({
  label,
  value,
  caption,
  tone,
  onClick,
  active,
}: {
  label: string
  value: ReactNode
  caption?: ReactNode
  tone?: 'red' | 'amber' | 'green'
  onClick?: () => void
  active?: boolean
}) {
  const body = (
    <>
      <span className="block text-[11px] font-semibold tracking-wider text-ink-500 uppercase">
        {label}
      </span>
      <span className="flex flex-wrap items-baseline gap-x-2">
        <span
          className={cn(
            'font-display text-lg font-extrabold tabular-nums',
            tone === 'red'
              ? 'text-red-700'
              : tone === 'amber'
                ? 'text-amber-700'
                : tone === 'green'
                  ? 'text-emerald-700'
                  : 'text-brand-800',
          )}
        >
          {value}
        </span>
        {caption ? <span className="min-w-0 text-xs text-ink-500">{caption}</span> : null}
      </span>
    </>
  )

  const base = 'min-w-0 flex-1 px-[var(--card-pad)] py-2 text-left'

  return onClick ? (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        base,
        'transition-colors hover:bg-ink-50',
        active && 'bg-brand-50 shadow-[inset_0_-3px_0_var(--color-accent-500)] hover:bg-brand-50',
      )}
    >
      {body}
    </button>
  ) : (
    <div className={base}>{body}</div>
  )
}

/** Stat tiles in one card, divided by hairlines, wrapping to two up on a phone. */
export function StatStrip({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <Card className={cn('grid grid-cols-2 divide-ink-100 sm:flex sm:divide-x', className)}>
      {children}
    </Card>
  )
}

/**
 * Column names over a list. Shown from `sm` up, where the row is a grid with
 * the same template; a phone row stacks and needs no headings.
 */
export function ListHead({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div
      className={cn(
        'hidden items-center gap-3 border-b border-ink-100 bg-ink-50/60 px-[var(--row-pad-x)] py-1.5 text-[11px] font-semibold tracking-wider text-ink-500 uppercase sm:grid',
        className,
      )}
    >
      {children}
    </div>
  )
}

/**
 * The shared row skin for a list. A grid from `sm` up, with the caller's
 * template; on a phone the cells flow and wrap, so a short cell sits beside
 * the one before it rather than taking a line of its own. A cell that should
 * own its line on a phone says `basis-full sm:basis-auto`.
 */
export const listRowClass =
  'flex flex-wrap items-center gap-x-2 gap-y-0.5 px-[var(--row-pad-x)] py-[var(--row-pad-y)] transition-colors hover:bg-ink-50 sm:grid sm:gap-3'

/** A label and its value, in a record's details card. */
export function Detail({
  label,
  children,
  className,
}: {
  label: string
  children: ReactNode
  className?: string
}) {
  return (
    <div className={cn('min-w-0', className)}>
      <dt className="text-[11px] font-semibold tracking-wider text-ink-500 uppercase">{label}</dt>
      <dd className="mt-0.5 text-sm break-words text-ink-900">{children}</dd>
    </div>
  )
}

/**
 * A labelled setting with its switch: the name, a line on what it does, and
 * `Toggle` at the end of the row. The switch's accessible name is the label,
 * so the visible text and what a screen reader says agree.
 */
export function ToggleRow({
  label,
  description,
  checked,
  onChange,
  disabled,
}: {
  label: string
  description?: ReactNode
  checked: boolean
  onChange: (checked: boolean) => void
  disabled?: boolean
}) {
  return (
    <div className="flex items-start gap-3">
      <span className="min-w-0 flex-1">
        <span className={cn('block text-sm font-medium', disabled ? 'text-ink-500' : 'text-ink-900')}>
          {label}
        </span>
        {description ? (
          <span className={cn('mt-px block text-xs', disabled ? 'text-ink-400' : 'text-ink-500')}>
            {description}
          </span>
        ) : null}
      </span>
      <Toggle label={label} checked={checked} onChange={onChange} disabled={disabled} />
    </div>
  )
}
